import { Downsampler, Upsampler } from './pcm';

/** Microphone in and speaker out for a bridged call, at 8 kHz PCM16. */
export interface AudioIO {
  /** Ask for the microphone and start; `onFrame` receives 20 ms frames. */
  start(onFrame: (frame: Int16Array) => void): Promise<void>;
  /** Play one frame of the caller's audio. */
  play(frame: Int16Array): void;
  setMuted(muted: boolean): void;
  stop(): void;
}

/**
 * Two small AudioWorklet processors, loaded from a Blob so no extra asset is
 * served: one hands microphone samples to the page, one plays queued caller
 * audio with a short jitter buffer (refilled after an underrun, trimmed when
 * a backlog builds up so the conversation stays live).
 */
const WORKLET_SOURCE = `
class BridgeCapture extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel) this.port.postMessage(channel.slice(0));
    return true;
  }
}
registerProcessor('bridge-capture', BridgeCapture);

class BridgePlayer extends AudioWorkletProcessor {
  constructor() {
    super();
    this.queue = [];
    this.offset = 0;
    this.buffered = 0;
    this.playing = false;
    this.port.onmessage = (event) => {
      this.queue.push(event.data);
      this.buffered += event.data.length;
      while (this.buffered > sampleRate * 0.5 && this.queue.length > 1) {
        this.buffered -= this.queue.shift().length - this.offset;
        this.offset = 0;
      }
    };
  }
  process(_inputs, outputs) {
    const out = outputs[0][0];
    if (!this.playing) {
      if (this.buffered < sampleRate * 0.06) { out.fill(0); return true; }
      this.playing = true;
    }
    let i = 0;
    while (i < out.length && this.queue.length) {
      const head = this.queue[0];
      const n = Math.min(out.length - i, head.length - this.offset);
      out.set(head.subarray(this.offset, this.offset + n), i);
      i += n; this.offset += n; this.buffered -= n;
      if (this.offset >= head.length) { this.queue.shift(); this.offset = 0; }
    }
    if (i < out.length) { out.fill(0, i); this.playing = false; }
    return true;
  }
}
registerProcessor('bridge-player', BridgePlayer);
`;

export class WebAudioIO implements AudioIO {
  private context?: AudioContext;
  private stream?: MediaStream;
  private capture?: AudioWorkletNode;
  private player?: AudioWorkletNode;
  private upsampler?: Upsampler;
  private muted = false;

  async start(onFrame: (frame: Int16Array) => void): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    });
    const context = new AudioContext();
    this.context = context;

    const moduleUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
    try {
      await context.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }

    const downsampler = new Downsampler(context.sampleRate);
    this.upsampler = new Upsampler(context.sampleRate);

    this.capture = new AudioWorkletNode(context, 'bridge-capture');
    this.capture.port.onmessage = (event: MessageEvent<Float32Array>) => {
      for (const frame of downsampler.push(event.data)) {
        onFrame(this.muted ? new Int16Array(frame.length) : frame);
      }
    };
    // The capture node must be pulled by the graph to run; route it to a
    // silent gain so the agent does not hear themselves.
    const sink = context.createGain();
    sink.gain.value = 0;
    context.createMediaStreamSource(this.stream).connect(this.capture);
    this.capture.connect(sink).connect(context.destination);

    this.player = new AudioWorkletNode(context, 'bridge-player', { outputChannelCount: [1] });
    this.player.connect(context.destination);

    if (context.state === 'suspended') await context.resume();
  }

  play(frame: Int16Array): void {
    if (!this.player || !this.upsampler) return;
    this.player.port.postMessage(this.upsampler.push(frame));
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  stop(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.capture?.disconnect();
    this.player?.disconnect();
    void this.context?.close().catch(() => undefined);
    this.stream = undefined;
    this.context = undefined;
  }
}
